import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { autoserializeAs,Deserialize,IJsonObject,Serialize } from 'dcerialize';
import { BehaviorSubject, Subject } from 'rxjs/';
import { Observable } from 'rxjs/internal/Observable';
import { catchError, map } from 'rxjs/operators';
import { ApiService } from 'src/services/api.service';

import { CredentialsInterface } from '../../../interfaces/user.interface';
import { User, UserPassword } from '../../../models/user';
import { CustomSnackbarService } from '../../../services/custom-snackbar.service';
import { UserService } from '../../../services/user.service';
import { VerticalSelectorService } from '../../../services/vertical-selector.service';
import { getStorageObject, removeStorageObject, setStorageObject } from '../../../utils/storage-manager';

/**
 * Update password response structure
 */
interface UpdatePasswordResponse {
  /**
   * If the update was successful or not
   */
  success: boolean;
}
/**
 * Reset password response structure
 */
interface ResetPasswordResponse {
  /**
   * Email associated with the token
   */
  email: string;
}
/**
 * Login response structure
 */
export class LoginResponse {
  /**
   * If the login was successful or not
   */
  @autoserializeAs(() => Boolean, 'login_ok') loginOk: boolean | undefined;

  /**
   * Login access token
   */
  @autoserializeAs(() => String, 'access_token') accessToken: string | undefined;

  /**
   * If the user is blocked or not
   */
  @autoserializeAs(() => Boolean, 'blocked') blocked: boolean | undefined;
}

/**
 * Authentication service using JWT
 */
@Injectable()
export class AuthService {
  /**
   * The Observable with accessToken
   */
  private readonly accessToken: BehaviorSubject<string>;

  /**
   * The Observable signaling that the user has logged out
   */
  public userLoggedOut: Subject<boolean> = new Subject<boolean>();

  /**
   * Url to redirect after login
   */
  public redirectUrl: string | null = null;

  /**
   * The Observable with a valid email address to reset the password
   */

  private resetTokenValid: BehaviorSubject<string> = new BehaviorSubject<string>('');
  /**
   * Constructor
   */
  constructor(
    private http: HttpClient,
    private apiService: ApiService,
    private snackBarService: CustomSnackbarService,
    private userService: UserService,
    private verticalSelectorService: VerticalSelectorService
  ) {
    this.accessToken = new BehaviorSubject(String());
    this.apiService = new ApiService(verticalSelectorService);
    this.userService = new UserService(http, snackBarService, apiService);
  }

  /**
   * This method stores the accessToken retrieved from the backend in the local storage
   * @param credentials - An object with the keys 'username' and 'password'
   * @returns The data object with the accessToken or an error
   */
  public login(credentials: CredentialsInterface): Observable<string> {
    this.http
      .post<IJsonObject>(this.apiService.getApiUrl() + 'auth/login', credentials, {})
      .pipe(
        map((response) => Deserialize(response, () => LoginResponse)),
        catchError((err: HttpErrorResponse) => this.snackBarService.showError(err))
      )
      .subscribe(
        // We're assuming the response will be an object
        // with the JWT on an accessToken key
        (data) => {
          if (data.loginOk && data.accessToken) {
            const storage = credentials.remember ? 'local' : 'session';
            setStorageObject('remember', { value: credentials.remember }, credentials.remember ? 'local' : 'session');
            setStorageObject('access_token', data.accessToken, storage);
            this.fillUserData().subscribe(() => this.accessToken.next(<string>data.accessToken));
          } else if (data.blocked === true) {
            this.accessToken.next(data.blocked.toString());
          } else {
            this.accessToken.next('');
          }
        },
        (_error) => {
          console.log('Error in login :', _error);
          this.accessToken.next('');
        }
      );

    return this.accessToken;
  }
  /**
   * Fill user data with full profile from the database
   */
  public fillUserData(): Observable<boolean> {
    const dataLoaded = new Subject<boolean>();

    this.userService.profile().subscribe((user: User) => {
      const remember = getStorageObject('remember');
      setStorageObject(
        'userData',
        Serialize(user, () => User),
        remember.value ? 'local' : 'session'
      );
      dataLoaded.next(true);
    });

    return dataLoaded.asObservable();
  }

  /**
   * Method to set the current user passing a user by parameter
   * @param user
   */
  public setCurrentUser(user: User): void {
    const remember = getStorageObject('remember');
    setStorageObject('userData', user, remember.value ? 'local' : 'session');
  }
  /**
   * Method that retrieves the user data from the JWT
   *
   * @return Object User data
   */
  public getUserData(): any {
    return Deserialize(getStorageObject('userData'), () => User);
  }
  /**
   * Method that checks if a reset password token is valid
   * @param token Reset password token
   * @return Observable<any> Observable with a valid email, or null if token is invalid
   */
  public checkResetPasswordToken(token: string): Observable<any> {
    this.http.get<ResetPasswordResponse>(this.apiService.getApiUrl() + 'user/password/' + token).subscribe(
      (data) => {
        if (data.email && data.email !== '') {
          this.resetTokenValid.next(data.email);
        } else {
          this.resetTokenValid.next('');
        }
      },
      (_error) => {
        this.resetTokenValid.next('');
      }
    );

    return this.resetTokenValid.asObservable();
  }

  /**
   * Method to reset the user password using a token
   *
   * @param token - string  Reset password token
   * @param newPassword Combination of 'password' and 'repeat_password' (confirmation)
   * @return Observable<any> Observable indicating if the change was successful or not
   */
  public resetPassword(token: string, newPassword: UserPassword): Observable<any> {
    const options = {
      headers: new HttpHeaders().set('Content-Type', 'application/json')
    };

    return this.http.patch<UpdatePasswordResponse>(
      this.apiService.getApiUrl() + 'user/password/' + token,
      Serialize(newPassword, () => UserPassword),
      options
    );
  }

  /**
   * Method to start the forgot password process
   *
   * @param restoreEmail Email to restore the password
   * @return Observable<null> Observable indicating if the change was successful or not
   */
  public forgotPassword(restoreEmail: string): Observable<null> {
    const options = {
      headers: new HttpHeaders().set('Content-Type', 'application/json')
    };

    return this.http
      .patch<null>(this.apiService.getApiUrl() + 'user/password', { email: restoreEmail }, options)
      .pipe(catchError((err: HttpErrorResponse) => this.snackBarService.showError(err)));
  }

  /**
   * Whether the user has a valid token or not
   */
  public loggedIn(): boolean {
    return !!getStorageObject('access_token');
  }

  /**
   * Remove the token from local storage and redirects the user to login page
   */
  public logout(): void {
    removeStorageObject('access_token');
    removeStorageObject('remember');
    removeStorageObject('region');
    this.verticalSelectorService.setVertical(null);
    this.userLoggedOut.next(true);
  }

  public hasPermission(role: string): boolean {
    const currentUserData: User = new User();
    currentUserData.roles = this.getUserData()['roles'];

    return currentUserData.hasPermission(role);
  }
}
